import { useMemo } from 'react'
import { useSubscription } from '../contexts/SubscriptionContext'
import { useSystemConfig } from './useSystemConfig'

export interface TrialStatus {
  isInTrial: boolean
  trialEndDate: Date | null
  daysLeft: number
  isExpiringSoon: boolean // X dias ou menos (configurável)
  isExpiringToday: boolean
  hasExpired: boolean
  trialProgress: number // Porcentagem de uso (0-100)
}

/**
 * Hook para gerenciar o status do trial do usuário
 * Retorna informações sobre o trial incluindo dias restantes e alertas
 * Agora usa configurações do banco de dados para duração e avisos
 */
export const useTrialStatus = (): TrialStatus => {
  const { isTrialing, trialEndDate, daysLeftInTrial, isActive, loading, hadExpiredTrial, expiredTrialEndDate } = useSubscription()
  const { getNumber } = useSystemConfig()

  // Get trial configuration from database
  const totalTrialDays = getNumber('trial_duration_days', 7)
  const warningDays = getNumber('trial_warning_days', 3)

  const trialStatus = useMemo(() => {
    // While subscription data is still loading, return safe defaults
    // to prevent false hasExpired during initialization race condition
    if (loading) {
      return {
        isInTrial: false,
        trialEndDate: null,
        daysLeft: 0,
        isExpiringSoon: false,
        isExpiringToday: false,
        hasExpired: false,
        trialProgress: 0
      }
    }

    // Se não está em trial e não tem subscription ativa
    if (!isTrialing && !isActive) {
      // hadExpiredTrial cobre tanto a janela pré-cron (status 'trialing' com
      // trial_end passado) quanto pós-cron (status 'canceled', subscription null)
      return {
        isInTrial: false,
        trialEndDate: hadExpiredTrial ? expiredTrialEndDate : null,
        daysLeft: 0,
        isExpiringSoon: false,
        isExpiringToday: false,
        hasExpired: hadExpiredTrial, // Só marca como expirado se realmente teve um trial
        trialProgress: hadExpiredTrial ? 100 : 0
      }
    }

    // Se não está em trial mas tem subscription ativa, converteu
    if (!isTrialing && isActive) {
      return {
        isInTrial: false,
        trialEndDate: null,
        daysLeft: 0,
        isExpiringSoon: false,
        isExpiringToday: false,
        hasExpired: false,
        trialProgress: 100
      }
    }

    // Está em trial ativo
    const daysLeft = daysLeftInTrial || 0
    const daysUsed = totalTrialDays - daysLeft
    const trialProgress = Math.min(100, Math.max(0, (daysUsed / totalTrialDays) * 100))

    return {
      isInTrial: true,
      trialEndDate: trialEndDate || null,
      daysLeft,
      isExpiringSoon: daysLeft <= warningDays && daysLeft > 0,
      isExpiringToday: daysLeft === 0,
      hasExpired: false,
      trialProgress
    }
  }, [isTrialing, trialEndDate, daysLeftInTrial, isActive, loading, totalTrialDays, warningDays, hadExpiredTrial, expiredTrialEndDate])

  return trialStatus
}

/**
 * Hook simplificado para verificar se deve mostrar avisos de trial
 */
export const useShouldShowTrialWarning = (): boolean => {
  const { isExpiringSoon, isExpiringToday, isInTrial } = useTrialStatus()
  return isInTrial && (isExpiringSoon || isExpiringToday)
}

/**
 * Hook para obter mensagem de alerta baseada no status do trial
 */
export const useTrialAlertMessage = (): string | null => {
  const { daysLeft, isExpiringSoon, isExpiringToday, hasExpired, isInTrial } = useTrialStatus()

  if (hasExpired) {
    return 'Seu trial expirou. Escolha um plano para continuar usando a plataforma.'
  }

  if (isExpiringToday) {
    return 'Seu trial expira hoje! Assine agora para não perder o acesso.'
  }

  if (isExpiringSoon) {
    return daysLeft === 1
      ? 'Falta apenas 1 dia no seu trial. Não perca tempo!'
      : `Faltam apenas ${daysLeft} dias no seu trial. Não perca tempo!`
  }

  // Qualquer trial ativo fora da janela de aviso — sem depender do valor
  // configurável de trial_warning_days (antes o `> 3` hardcoded abria uma
  // janela sem mensagem quando a config divergia de 3)
  if (isInTrial) {
    return daysLeft === 1
      ? 'Você tem 1 dia restante no seu trial gratuito.'
      : `Você tem ${daysLeft} dias restantes no seu trial gratuito.`
  }

  return null
}

/**
 * Hook para obter cor do alerta baseada na urgência
 */
export const useTrialAlertColor = (): 'green' | 'yellow' | 'orange' | 'red' => {
  const { daysLeft, hasExpired } = useTrialStatus()

  if (hasExpired) return 'red'
  if (daysLeft === 0) return 'red'
  if (daysLeft <= 1) return 'orange'
  if (daysLeft <= 3) return 'yellow'
  return 'green'
}
